<div class="deliverable-container" [ngClass]="{ 'submitted': isSubmitted }">
  <div class="deliverable-header">
    <span class="deliverable-badge">LIVRABLE FINAL</span>
    <h3>Questions pour votre livrable personnalisé</h3>
  </div>
  <p class="subtitle">Ces réponses construiront votre document de fin de formation.</p>

  <div *ngIf="isLoading" class="loading-container">
    <div class="loader"></div>
    <span>Chargement de vos réponses...</span>
  </div>

  <form *ngIf="!isLoading" [formGroup]="deliverableForm" (ngSubmit)="onSubmit()">
    <div *ngFor="let question of deliverableQuestions; let qIndex = index" class="question-block">
      <div class="question-header">
        <span *ngIf="question.mandatory" class="mandatory-badge">Obligatoire</span>
        <span *ngIf="!question.mandatory" class="optional-badge">Facultatif</span>
      </div>
      <p class="question-text">{{ question.question }}</p>
      <p *ngIf="question.description" class="question-description">{{ question.description }}</p>

      <ng-container [ngSwitch]="question.questionType">
        <div *ngSwitchCase="'freeText'" class="answer-container">
          <textarea
            [formControlName]="'Q' + qIndex"
            [disabled]="isSubmitted"
            placeholder="Votre réponse..."
            rows="4"
          ></textarea>
        </div>

        <div *ngSwitchCase="'fillInTheBlank'" class="answer-container fill-blank-container">
          <ng-container *ngIf="question.templateText && getBlankCount(question.templateText) > 0; else noTemplate">
            <ng-container *ngFor="let part of getBlankParts(question.templateText); let pIndex = index">
              <span class="text-part">{{ part }}</span>
              <input
                *ngIf="pIndex < getBlankCount(question.templateText)"
                type="text"
                class="blank-input"
                [formControl]="getBlankControl(qIndex, pIndex)"
                [disabled]="isSubmitted"
                placeholder="..."
              />
            </ng-container>
          </ng-container>
          <ng-template #noTemplate>
            <p class="error-text">Erreur : le template de cette question n'est pas configuré correctement.</p>
          </ng-template>
        </div>

        <div *ngSwitchCase="'table'" class="answer-container table-container">
          <table [formGroupName]="'Q' + qIndex">
            <thead>
              <tr>
                <th *ngIf="question.tableRowLabels?.length"></th>
                <th *ngIf="question.firstColumnAsNumbering && question.tableColumns?.length">{{ question.tableColumns[0].label }}</th>
                <ng-container *ngFor="let col of question.tableColumns; let colIndex = index">
                  <th *ngIf="!question.firstColumnAsNumbering || colIndex > 0">{{ col.label }}</th>
                </ng-container>
                <th *ngIf="!question.tableRowLabels?.length && canRemoveRow(qIndex)" class="action-cell"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let rowIndex of getTableRows(qIndex); let i = index">
                <td *ngIf="question.tableRowLabels?.length" class="row-label">
                  {{ question.tableRowLabels[i] || '' }}
                </td>
                <td *ngIf="question.firstColumnAsNumbering" class="row-label numbering-cell">
                  {{ i + 1 }}
                </td>
                <ng-container *ngFor="let col of question.tableColumns; let colIndex = index">
                  <td *ngIf="!question.firstColumnAsNumbering || colIndex > 0">
                    <input
                      type="text"
                      [formControl]="getTableCellControl(qIndex, col.label, i)"
                      [disabled]="isSubmitted"
                    />
                  </td>
                </ng-container>
                <td *ngIf="!question.tableRowLabels?.length && canRemoveRow(qIndex)" class="action-cell">
                  <button
                    type="button"
                    class="remove-row-btn"
                    (click)="removeTableRow(qIndex, i)"
                    [disabled]="isSubmitted"
                    title="Supprimer cette ligne"
                  >
                    ×
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <button
            *ngIf="!question.tableRowLabels?.length && !(question.tableMinRows && question.tableMaxRows && question.tableMinRows === question.tableMaxRows)"
            type="button"
            class="add-row-btn"
            (click)="addTableRow(qIndex)"
            [disabled]="isSubmitted || !canAddRow(qIndex)"
          >
            + Ajouter une ligne
          </button>
        </div>

        <div *ngSwitchCase="'imageUpload'" class="answer-container image-container" [formGroupName]="'Q' + qIndex">
          <div class="image-options">
            <div class="upload-zone" *ngIf="!deliverableForm.get('Q' + qIndex).get('useAiGeneration').value">
              <input
                type="file"
                accept="image/*"
                [disabled]="isSubmitted || uploadingByQuestion[qIndex]"
                (change)="onImageUpload($event, qIndex)"
                [id]="'fileInput-' + qIndex"
                class="hidden-file-input"
              />
              <div class="upload-placeholder" (click)="!uploadingByQuestion[qIndex] && triggerFileInput(qIndex)" [class.uploading]="uploadingByQuestion[qIndex]">
                <span *ngIf="uploadingByQuestion[qIndex]">Upload en cours…</span>
                <span *ngIf="!uploadingByQuestion[qIndex] && !deliverableForm.get('Q' + qIndex).get('imageUrl').value">
                  Cliquez pour uploader une image
                </span>
                <img
                  *ngIf="!uploadingByQuestion[qIndex] && deliverableForm.get('Q' + qIndex).get('imageUrl').value"
                  [src]="deliverableForm.get('Q' + qIndex).get('imageUrl').value"
                  class="preview-image"
                />
              </div>
              <p *ngIf="uploadErrorByQuestion[qIndex]" class="upload-error">{{ uploadErrorByQuestion[qIndex] }}</p>
            </div>
            <div *ngIf="question.allowAiGeneration" class="ai-option">
              <label>
                <input
                  type="checkbox"
                  formControlName="useAiGeneration"
                  [disabled]="isSubmitted"
                />
                Laisser l'IA générer une image
              </label>
            </div>
          </div>
        </div>
      </ng-container>
    </div>

    <div *ngIf="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="form-actions">
      <ng-container *ngIf="!isSubmitted">
        <span class="autosave-indicator saving" *ngIf="saveStatus === 'saving'">Enregistrement...</span>
        <span class="autosave-indicator saved" *ngIf="saveStatus === 'saved'">Brouillon enregistré ✓</span>
        <app-button type="submit" theme="info" text="Valider mes réponses" size="md"></app-button>
      </ng-container>
      <ng-container *ngIf="isSubmitted">
        <app-button
          theme="info"
          [outlined]="true"
          text="Modifier mes réponses"
          size="md"
          (onClick)="onModify()"
        ></app-button>
        <app-button
          theme="info"
          [outlined]="true"
          text="Récap livrables"
          size="md"
          (onClick)="goToRecap()"
        ></app-button>
        <app-button
          theme="info"
          [text]="isSelectedCourseLast ? 'Compléter le module' : 'Cours suivant'"
          size="md"
          (onClick)="onNext()"
        ></app-button>
      </ng-container>
    </div>
  </form>
</div>
